/* eslint-disable prettier/prettier */
import { useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import styles from '../styles/pages/LoginError.module.css';
import {
	clearStoredCredentials,
	getAuthErrorCopy,
	getProviderLabel,
} from '../constants/authError.constant';

const formatDeletedAt = (value: string | null): string | null => {
	if (!value) {
		return null;
	}
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return null;
	}
	return date.toLocaleString(undefined, {
		day: 'numeric',
		month: 'long',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});
};

const LoginError = () => {
	const [searchParams] = useSearchParams();

	const reason = searchParams.get('reason');
	const provider = searchParams.get('provider');
	const email = searchParams.get('email');
	const deletedAt = formatDeletedAt(searchParams.get('deletedat'));

	const copy = useMemo(() => getAuthErrorCopy(reason), [reason]);
	const providerLabel = getProviderLabel(provider);

	// A refused sign-in must not leave a half-valid session behind.
	useEffect(() => {
		clearStoredCredentials();
	}, []);

	return (
		<div className={styles.page}>
			<div className={styles.card} role="alert" aria-live="polite">
				<div className={styles.iconWrap} aria-hidden="true">
					<svg viewBox="0 0 24 24" className={styles.icon}>
						<circle
							cx="12"
							cy="12"
							r="10"
							fill="none"
							stroke="currentColor"
							strokeWidth="1.6"
						/>
						<path
							d="M12 7v6"
							stroke="currentColor"
							strokeWidth="1.8"
							strokeLinecap="round"
						/>
						<circle cx="12" cy="16.4" r="1.05" fill="currentColor" />
					</svg>
				</div>

				<h1 className={styles.title}>{copy.title}</h1>
				<p className={styles.description}>{copy.description}</p>

				{(email || provider || deletedAt) && (
					<dl className={styles.details}>
						{provider && (
							<div className={styles.detailRow}>
								<dt className={styles.detailLabel}>Signed in with</dt>
								<dd className={styles.detailValue}>{providerLabel}</dd>
							</div>
						)}
						{email && (
							<div className={styles.detailRow}>
								<dt className={styles.detailLabel}>Account</dt>
								<dd className={styles.detailValue}>{email}</dd>
							</div>
						)}
						{deletedAt && (
							<div className={styles.detailRow}>
								<dt className={styles.detailLabel}>Deleted on</dt>
								<dd className={styles.detailValue}>{deletedAt}</dd>
							</div>
						)}
					</dl>
				)}

				<p className={styles.action}>{copy.action}</p>

				<div className={styles.buttons}>
					<Link to="/login" className={styles.primaryButton}>
						{copy.canRetry ? 'Try again' : 'Back to login'}
					</Link>
					
				</div>
			</div>
		</div>
	);
};

export default LoginError;
